import React, { useEffect, useState } from "react";
import { useToast } from "../../context/ToastContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../lib/api.ts";
import { formatPrice } from "../../utils/functions";

const SellerInvoices = () => {
  const { showSuccess, showError } = useToast();
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [withdraw, setWithdraw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleWithdrawRequest = () => {
    // Open confirmation toast first
    setShowConfirm(true);
  };

  const handleConfirmWithdraw = async () => {
    if (withdraw) return;
    try {
      setWithdraw(true);
      setError(null);
      await api.post(`/sellerwithdrawalinvoices/withdrawrequest`);
      showSuccess(
        translate("sellerInvoices.messages.withdrawRequestSubmitted"),
      );
    } catch (err) {
      setError(translate("sellerInvoices.messages.withdrawRequestFailed"));
      showError(translate("sellerInvoices.messages.withdrawRequestFailed"));
    } finally {
      setWithdraw(false);
      setShowConfirm(false);
    }
  };

  useEffect(() => {
    const fetchSellerInvoices = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/sellerwithdrawalinvoices/paginated`, {
          params: { orderBy: "id", sort: "desc", page: 1, pageSize: 20 },
        });

        const data = response?.data?.data || response?.data || [];
        setInvoices(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(translate("sellerInvoices.messages.failedToLoad"));
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSellerInvoices();
  }, []);

  const getStatusText = (status) => {
    if (!status) return "-";
    const statusMap = {
      Paid: translate("sellerInvoices.status.paid"),
      Pending: translate("sellerInvoices.status.pending"),
      "Partial Payment": translate("sellerInvoices.status.partialPayment"),
      Cancelled: translate("sellerInvoices.status.cancelled"),
      Refunded: translate("sellerInvoices.status.refunded"),
      Rejected: "Rejected",
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "-") return "-";
    return new Date(dateString).toLocaleDateString(
      language === "ar" ? "ar-AE" : "en-US",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      },
    );
  };

  return (
    <div
      className="bg-white rounded-lg shadow border border-gray-200"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Confirm Toast */}
      {showConfirm && (
        <div className={`fixed top-6 z-50 ${isRTL ? "left-6" : "right-6"}`}>
          <div className="px-4 py-3 rounded shadow bg-white border border-gray-200">
            <div className="text-sm text-gray-800 mb-2">
              {translate("sellerInvoices.messages.confirmWithdraw")}
            </div>
            <div
              className={`flex gap-2 ${
                isRTL ? "justify-start" : "justify-end"
              }`}
            >
              <button
                onClick={() => setShowConfirm(false)}
                className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 text-sm"
              >
                {translate("sellerInvoices.buttons.cancel")}
              </button>
              <button
                onClick={handleConfirmWithdraw}
                disabled={withdraw}
                className={`px-3 py-1.5 rounded text-white text-sm ${
                  withdraw ? "bg-gray-400" : "bg-primary hover:opacity-90"
                }`}
              >
                {withdraw
                  ? translate("sellerInvoices.buttons.submitting")
                  : translate("sellerInvoices.buttons.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        className={`px-4 py-3 border-b border-gray-200 flex items-center ${
          isRTL ? "flex-row-reverse" : "justify-between"
        }`}
      >
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">
          {translate("sellerInvoices.heading")}
        </h2>
        {/* <button
          onClick={handleWithdrawRequest}
          disabled={withdraw}
          className={`px-4 py-2 rounded-md text-white ${
            withdraw
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-primary hover:opacity-90"
          }`}
        >
          {withdraw
            ? translate("sellerInvoices.buttons.submitting")
            : translate("sellerInvoices.buttons.requestWithdraw")}
        </button> */}
      </div>

      {loading ? (
        <div className="p-6 text-sm text-gray-500">
          {translate("sellerInvoices.messages.loading")}
        </div>
      ) : error ? (
        <div className="p-6 text-sm text-red-600">{error}</div>
      ) : invoices.length === 0 ? (
        <div className="p-6 text-sm text-gray-500">
          {translate("sellerInvoices.messages.noInvoicesFound")}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className={`px-4 py-2 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerInvoices.tableHeaders.vehicle")}
                </th>

                <th
                  className={`px-4 py-2 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerInvoices.tableHeaders.totalAmount")}
                </th>
                <th
                  className={`px-4 py-2 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerInvoices.tableHeaders.status")}
                </th>
                <th
                  className={`px-4 py-2 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerInvoices.tableHeaders.date")}
                </th>
{/* ===============tuesday fix show receipt */}
                <th
                  className={`px-4 py-2 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerInvoices.tableHeaders.action")}
                </th>
                {/* ===============tuesday end=============*/}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {invoices.map((inv) => {
                // Get vehicle name from sellerInvoiceDetails array
                // const vehicleName =
                //   inv?.sellerInvoiceDetails?.[0]?.vehicle?.name ?? "-";
                const vehicleName =
                  inv?.sellerWithdrawalInvoiceDetails?.[0]?.vehicle?.name ??
                  "-";

                const lotNumber =
                  inv?.sellerWithdrawalInvoiceDetails?.[0]?.vehicle
                    ?.lotNumber ?? "-";
                const id = inv?.id ?? "-";
                const date = inv?.createdAt ?? "-";
                const amount = inv?.totalAmount ?? 0;
                const status = inv?.invoicePaymentStatus?.name ?? "-";

                return (
                  <tr key={inv?.id} className='hover:bg-gray-50'>
                    <td className='px-4 py-2 text-sm text-gray-700'>
                      <div className='flex flex-col'>
                        <span className='font-medium'>{vehicleName}</span>
                        <span className='text-xs text-gray-500'>
                          Lot No: {lotNumber}
                        </span>
                      </div>
                    </td>

                    <td className='px-4 py-2 text-sm font-medium text-gray-900'>
                      {formatPrice(amount)}
                    </td>
                    <td className='px-4 py-2 text-xs'>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          status === "Paid"
                            ? "bg-green-700 text-white"
                            : status === "Pending"
                              ? "bg-yellow-700 text-white"
                              : status === "Partial Payment"
                                ? "bg-blue-700 text-white"
                                : status === "Cancelled"
                                  ? "bg-red-700 text-white"
                                  : status === "Refunded"
                                    ? "bg-purple-700 text-white"
                                    : "bg-gray-700 text-white"
                        }`}
                      >
                        {getStatusText(status)}
                      </span>
                      {status === "Rejected" && inv?.rejectionReason && (
                        <div
                          className='text-red-500 mt-1 text-[10px]'
                          style={{ maxWidth: "150px" }}
                        >
                          Reason: {inv.rejectionReason}
                        </div>
                      )}
                    </td>
                    <td className='px-4 py-2 text-sm text-gray-700'>
                      {formatDate(date)}
                    </td>
                    {/* ===============tuesday fix show receipt */}
                    <td className='px-4 py-2 text-sm text-gray-700'>
                      {inv?.receiptUrl ? (
                        <button
                          onClick={() => window.open(inv.receiptUrl, "_blank")}
                          className='px-4 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700'
                        >
                          View
                        </button>
                      ) : (
                        <span className='text-gray-400 text-xs'>
                          No Receipt
                        </span>
                      )}
                    </td>
                    {/* ===============tuesday end  */}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SellerInvoices;
